import PropTypes from "prop-types";
import React, { useMemo } from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { ListScroller } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import { applyOverrides } from "../applyOverrides";
import { commonPlacementComponents } from "../defines";
import { PlacementFactory } from "../PlacementFactory";
import { usePlacement } from "../usePlacement";
import { usePlacementIds } from "../usePlacementIds";
import { ListCallToAction } from "./ListCallToAction";
import { ListHeading } from "./ListHeading";
import styles from "./ListPlacements.module.scss";

export const CarouselPlacement = (props) => {
  const { placement, placementData = {} } = props;
  const { callToAction, items, listItemType, settings, title } = placement;
  const { analyticsValue = "" } = placementData;
  const { screenSize } = useDeviceInfo();
  const { setPlacementsAnalytics } = usePlacement();
  const memoizedItems = useMemo(() => getItems(), [items]);
  const gridItemIds = (usePlacementIds(new RegExp(`${placement?.id}*`)) || []).sort();

  return (
    <>
      <ListHeading title={title} />
      <ListScroller {...getListScrollerProps()} />
      <ListCallToAction callToAction={callToAction} />
    </>
  );

  function getListScrollerProps() {
    const { center, numberOfColumns, showPositionIndicator, yArrowOffset } = settings || {};

    return {
      "aria-label-current-page": i18n("CarouselPlacement__ARIA_LABEL__CURRENT_PAGE"),
      "aria-label-next": i18n("CarouselPlacement__ARIA_LABEL__NEXT"),
      "aria-label-page-count": i18n("CarouselPlacement__ARIA_LABEL__PAGE_COUNT"),
      "aria-label-previous": i18n("CarouselPlacement__ARIA_LABEL__PREVIOUS"),
      center,
      className: styles.content,
      items: memoizedItems,
      layout: "none",
      numberOfColumns,
      onClick: handleListScrollerClick,
      showPositionIndicator,
      yArrowOffset,
    };
  }

  function getItems() {
    const Component = commonPlacementComponents[listItemType];
    let content = null;

    if (placement?.id && gridItemIds?.length) {
      content = gridItemIds.map((itemId) => <PlacementFactory id={itemId} />);
    } else {
      content = items.map((item, index) => (
        <div className={styles.gridItem}>
          <Component key={index} placement={{ ...item }} />
        </div>
      ));
    }

    return content;
  }

  function handleListScrollerClick() {
    setPlacementsAnalytics({
      eventDescription: analyticsValue,
      eventType: "scroll",
    });
  }
};

CarouselPlacement.displayName = "CarouselPlacement";
CarouselPlacement.propTypes = {
  placement: PropTypes.shape({
    /** List of items to show in Carousel, object shape depends on item type. */
    items: PropTypes.arrayOf(PropTypes.object),

    /** Name of the item component to render. */
    listItemType: PropTypes.string,

    /** Placement configuration. */
    settings: PropTypes.shape({
      /** Center the carousel when possible, pass-through to ListScroller. */
      center: PropTypes.bool,

      /** Number of items show in Carousel, pass-through to ListScroller. */
      numberOfColumns: PropTypes.number,

      /** Override for all properties in settings by device screen size. */
      overrides: PropTypes.object,

      /** Wether or not to show the pages indicator, pass-through to ListScroller. */
      showPositionIndicator: PropTypes.bool,

      /** Aligns the left and arrow keys to the desired offset */
      yArrowOffset: PropTypes.number,
    }),
  }),
  placementData: PropTypes.shape({
    /** The dynamic value that will be used as an event description for analytics. */
    analyticsValue: PropTypes.string,
  }),
};
